import { useState, useEffect } from "react";
import { v4 as uuidv4 } from "uuid";
import { getFromStorage, setInStorage } from "@ROM-utils/storage";

/* Pagination is never part of a saved view - a view always opens on page 1. */
const captureFilters = (filters) => {
  const { page: _page, ...rest } = filters;
  return rest;
};

const useSavedViews = ({ storageKey, filters, initialFilters, setFilter }) => {
  const [views, setViews] = useState([]);
  const [activeViewId, setActiveViewId] = useState(null);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    setViews(getFromStorage(storageKey, []));
    setInitialized(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey]);

  useEffect(() => {
    if (!initialized) return;
    setInStorage(storageKey, views);
  }, [views, initialized, storageKey]);

  const applyView = (view) => {
    setFilter({ ...initialFilters, ...view.filters, page: 1 });
  };

  const addView = (name) => {
    const newView = {
      id: uuidv4(),
      name,
      filters: captureFilters(filters),
      createdAt: Date.now(),
      updatedAt: Date.now(),
    };

    setViews((prevViews) => [...prevViews, newView]);
    setActiveViewId(newView.id);
    return newView.id;
  };

  const updateView = (id, name) => {
    setViews((prevViews) =>
      prevViews.map((view) =>
        view.id === id ? { ...view, name, filters: captureFilters(filters), updatedAt: Date.now() } : view
      )
    );
  };

  const deleteView = (id) => {
    setViews((prevViews) => prevViews.filter((view) => view.id !== id));

    if (activeViewId === id) {
      setActiveViewId(null);
    }
  };

  const setActiveView = (id) => {
    if (id === activeViewId) return;

    setActiveViewId(id);

    if (id) {
      const view = views.find((v) => v.id === id);
      if (view) applyView(view);
    }
  };

  const reorderViews = (newOrder) => setViews(newOrder);

  return {
    views,
    activeViewId,
    addView,
    updateView,
    deleteView,
    setActiveView,
    reorderViews,
  };
};

export default useSavedViews;
